import { broadcastStateUpdate } from '../cast/MessageSender'

// Define the structure of our receiver state
export interface ReceiverState {
  recipe: any | null
  currentStepIndex: number
  showingIngredients: boolean
  timerActive: boolean
  timerRemaining?: number
  timerStepId?: string
  servings: number
  lastUpdated: string | null
}

// Initialize with default values
const initialState: ReceiverState = {
  recipe: null,
  currentStepIndex: 0,
  showingIngredients: false,
  timerActive: false,
  timerRemaining: undefined,
  timerStepId: undefined,
  servings: 4,
  lastUpdated: null
}

// Our current state
let currentState: ReceiverState = { ...initialState }

// Subscribers to state changes
const subscribers: Array<(state: ReceiverState) => void> = []

/**
 * Updates the receiver state
 * @param updater Either a partial state object or a function that returns a new state
 * @returns The updated state
 */
export function updateReceiverState(
  updater: Partial<ReceiverState> | ((state: ReceiverState) => ReceiverState)
): ReceiverState {
  // Apply the update
  if (typeof updater === 'function') {
    currentState = updater(currentState)
  } else {
    currentState = {
      ...currentState,
      ...updater
    }
  }
  
  // Debug log the updated state with safe JSON serialization
  try {
    console.log('State updated:', JSON.stringify(currentState))
    
    // Additional debug info for recipe
    if (currentState.recipe) {
      console.log('Recipe loaded:', true)
      console.log('Recipe title:', currentState.recipe.title)
      console.log('Recipe steps:', currentState.recipe.steps?.length || 0)
      console.log('Current step index:', currentState.currentStepIndex)
    } else {
      console.log('Recipe loaded:', false)
    }
  } catch (e) {
    console.log('State updated: [Could not stringify full state]')
    console.log('Recipe loaded:', !!currentState.recipe)
    console.log('Current step index:', currentState.currentStepIndex)
  }
  
  // Notify subscribers
  subscribers.forEach(subscriber => subscriber(currentState))
  
  // Broadcast the update to all senders if we have a recipe loaded
  if (currentState.recipe) {
    broadcastStateUpdate(currentState)
  }
  
  return currentState
}

/**
 * Gets the current receiver state
 */
export function getReceiverState(): ReceiverState {
  return { ...currentState }
}

/**
 * Resets the receiver state to initial values
 */
export function resetReceiverState(): ReceiverState {
  currentState = { ...initialState }
  subscribers.forEach(subscriber => subscriber(currentState))
  return currentState
}



/**
 * Subscribes to state changes
 * @param subscriber A callback function that will be called when state changes
 * @returns A function to unsubscribe
 */
export function subscribeToState(subscriber: (state: ReceiverState) => void): () => void {
  subscribers.push(subscriber)
  
  // Immediately call with current state
  subscriber(currentState)
  
  // Return unsubscribe function
  return () => {
    const index = subscribers.indexOf(subscriber)
    if (index !== -1) {
      subscribers.splice(index, 1)
    }
  }
}
